import React, { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

type CollapsibleItemProps = {
	children: React.ReactNode;
	defaultIsExpanded?: boolean;
	section?: string;
	source?: string;
	title: string;
};

const pressableStyles = xcss({
	background: 'none',
	height: 'space.300',
	paddingInline: 'space.100',
	paddingBlock: 'space.200',
});
const wrapperStyles = xcss({
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});
const expandedContainerStyles = xcss({
	paddingBottom: 'space.200',
	paddingInline: 'space.100',
});

export const CollapsibleItem = ({
	title,
	defaultIsExpanded,
	section,
	children,
	source,
}: CollapsibleItemProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isExpanded, setIsExpanded] = useState(defaultIsExpanded ?? false);

	const handleSectionClickAnalytics = useCallback(
		(newState: boolean, section?: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'collapsible',
					actionSubjectId: 'configPanelSectionCollapsible',
					source,
					attributes: {
						section,
						action: newState ? 'expanded' : 'collapsed',
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, source],
	);

	const handleClick = () => {
		handleSectionClickAnalytics(!isExpanded, section);
		setIsExpanded(!isExpanded);
	};

	return (
		<Stack space="space.100" xcss={wrapperStyles}>
			<Pressable onClick={handleClick} xcss={pressableStyles} testId="collapsible-item">
				<Flex justifyContent="space-between">
					<Text size="medium" weight="semibold">
						{title}
					</Text>
					{isExpanded ? (
						<ChevronUpIcon label="" color={token('color.icon')} size="small" />
					) : (
						<ChevronDownIcon label="" color={token('color.icon')} size="small" />
					)}
				</Flex>
			</Pressable>
			{isExpanded && (
				<Stack space="space.100" xcss={expandedContainerStyles}>
					{children}
				</Stack>
			)}
		</Stack>
	);
};
